if (!customElements.get('dk-sticky-purchase')) {
  customElements.define('dk-sticky-purchase', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.main = document.querySelector('product-info[id^="MainProduct-"]');
      if (!this.main || this.main.dataset.productId !== this.dataset.productId) return;
      this.queue = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
      };
      window.addEventListener('scroll', this.queue, { passive: true, signal });
      window.addEventListener('resize', this.queue, { passive: true, signal });
      document.addEventListener('focusin', this.queue, { signal });
      document.addEventListener('focusout', this.queue, { signal });
      document.addEventListener('click', this.queue, { signal });
      document.addEventListener('transitionend', this.queue, { signal });
      document.addEventListener('toggle', this.queue, { signal, capture: true });
      window.visualViewport?.addEventListener('resize', this.queue, { signal });
      this.actionButton = this.querySelector('.dk-sticky-purchase__button');
      this.actionButton.addEventListener('click', () => {
        const button = this.mainButton();
        const form = button?.form;
        if (!form || !form.querySelector('[name="id"]')?.value) return this.returnToPurchase();
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
        if (!form.reportValidity()) return this.returnToPurchase();
        button.click();
        this.queue();
      }, { signal });
      this.querySelector('.dk-sticky-purchase__change').addEventListener('click', () => this.returnToPurchase(), { signal });
      this.main.addEventListener('change', this.queue, { signal });
      this.main.addEventListener('input', this.queue, { signal });
      this.observer = new MutationObserver(this.queue);
      this.observer.observe(this.main, { childList: true, subtree: true, attributes: true, characterData: true, attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden', 'src'] });
      this.resizeObserver = new ResizeObserver(this.queue);
      this.resizeObserver.observe(this.main);
      this.queue();
    }
    mainButton() {
      return this.main?.querySelector('product-form button[name="add"]');
    }
    headerBottom() {
      const header = document.querySelector('.section-header, .shopify-section-header');
      if (!header) return 0;
      const rect = header.getBoundingClientRect();
      return rect.top <= 0 && rect.bottom > 0 ? rect.bottom : 0;
    }
    update() {
      const button = this.mainButton();
      const editing = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]),textarea,select,[contenteditable="true"]');
      const keyboard = window.visualViewport && window.innerHeight - window.visualViewport.height > 150;
      const blocked = Array.from(document.querySelectorAll('[role="alertdialog"],[aria-modal="true"],dialog[open]')).some(el => {
        if (el.closest('details:not([open]),[hidden],[aria-hidden="true"]')) return false;
        const style = getComputedStyle(el);
        return el.getClientRects().length > 0 && style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity) > 0;
      });
      this.syncDetails(button);
      this.hidden = !window.matchMedia('(max-width:749px)').matches || !button || !button.getClientRects().length || button.getBoundingClientRect().bottom >= this.headerBottom() || editing || keyboard || blocked;
    }
    syncDetails(button) {
      if (!button) return;
      const form = button.form;
      const variantId = form?.querySelector('[name="id"]')?.value;
      const pending = Boolean(this.main.pendingRequestUrl);
      const busy = button.getAttribute('aria-disabled') === 'true';
      this.actionButton.disabled = pending || busy || button.disabled;
      this.actionButton.setAttribute('aria-busy', String(pending || busy));
      this.actionButton.textContent = button.querySelector('span')?.textContent.trim() || button.textContent.trim();
      if (!variantId && !pending) {
        this.actionButton.disabled = false;
        this.actionButton.textContent = this.dataset.chooseLabel;
      }
      const priceRoot = this.main.querySelector('[id^="price-"] .price');
      const sale = priceRoot?.classList.contains('price--on-sale');
      const currentPrice = priceRoot?.querySelector(sale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
      const oldPrice = sale ? priceRoot?.querySelector('.price__sale .price-item--regular') : null;
      const price = this.querySelector('.dk-sticky-purchase__price');
      const compare = this.querySelector('.dk-sticky-purchase__compare');
      const priceText = currentPrice?.textContent.trim() || '';
      if (price.textContent !== priceText) price.textContent = priceText;
      const compareText = oldPrice?.textContent.trim() || '';
      if (compare.textContent !== compareText) compare.textContent = compareText;
      compare.hidden = !compareText;
      let variant;
      try { variant = JSON.parse(this.main.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null'); } catch {}
      const picker = this.main.querySelector('variant-selects');
      const variantLabel = this.querySelector('.dk-sticky-purchase__variant');
      const quantity = form?.elements.namedItem('quantity')?.value || '1';
      const title = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
      const detail = [title, Number(quantity) > 1 ? '× ' + quantity : ''].filter(Boolean).join(' · ');
      if (variantLabel.textContent !== detail) variantLabel.textContent = detail;
      variantLabel.hidden = !detail;
      this.querySelector('.dk-sticky-purchase__change').hidden = !picker;
      const image = this.querySelector('img');
      const imageUrl = variant?.featured_image?.src || this.dataset.defaultImage;
      if (image && imageUrl && image.dataset.variantImage !== imageUrl) {
        const url = new URL(imageUrl, window.location.origin);
        url.searchParams.set('width', '112');
        image.src = url.href;
        image.removeAttribute('srcset');
        image.dataset.variantImage = imageUrl;
      }
      const error = this.main.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message')?.textContent.trim() || '';
      const status = this.querySelector('.dk-sticky-purchase__status');
      if (status.textContent !== error) status.textContent = error;
      status.hidden = !error;
    }
    returnToPurchase() {
      const button = this.mainButton();
      if (!button) return;
      const picker = this.main.querySelector('variant-selects');
      const quantity = this.main.querySelector('quantity-input');
      const anchor = picker || quantity || button;
      const focusTarget = picker?.querySelector('input:checked:not(:disabled),select:not(:disabled)') || button;
      this.hidden = true;
      const header = document.querySelector('.section-header, .shopify-section-header');
      const headerSpace = Math.max(this.headerBottom(), header?.getBoundingClientRect().height || 0);
      const top = Math.max(0, anchor.getBoundingClientRect().top + window.scrollY - headerSpace - 20);
      focusTarget.focus({ preventScroll: true });
      window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth' });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  });
}
